import { webCrypto } from '#web-crypto';
import { RSA_CRYPTO_ENCRYPT_HASH, RSA_CRYPTO_MODULUS_SIZE, RSA_ENCRYPTION_ALGO_NAME, RSA_ENCRYPTION_ALGO_PARAMS, RSA_SIGNING_ALGO_PARAMS, } from './constants';
import { arrayToPrivateKey, arrayToPublicKey, cryptoKeyToJwk, privateKeyToArray, publicKeyToArray, } from './crypto-binary-utils';
import { InvalidCryptoKeyError } from './crypto-utils';
export const generateEncryptPair = async () => {
    return webCrypto.subtle.generateKey({
        name: RSA_ENCRYPTION_ALGO_NAME,
        modulusLength: RSA_CRYPTO_MODULUS_SIZE,
        publicExponent: new Uint8Array([1, 0, 1]),
        hash: RSA_CRYPTO_ENCRYPT_HASH,
    }, true, ['encrypt', 'decrypt']);
};
export const generateCryptoPairs = async () => {
    const encryptPair = await generateEncryptPair();
    const exportedPrivateKey = await privateKeyToArray(encryptPair.privateKey);
    const exportedPublicKey = await publicKeyToArray(encryptPair.publicKey);
    const signPair = {
        privateKey: await arrayToPrivateKey(exportedPrivateKey, RSA_SIGNING_ALGO_PARAMS),
        publicKey: await arrayToPublicKey(exportedPublicKey, RSA_SIGNING_ALGO_PARAMS),
    };
    return { encryptPair, signPair };
};
export const encryptionKeyPairFromArray = async (publicKey, privateKey) => {
    const [ePriv, ePub] = await Promise.all([
        arrayToPrivateKey(privateKey, RSA_ENCRYPTION_ALGO_PARAMS),
        arrayToPublicKey(publicKey, RSA_ENCRYPTION_ALGO_PARAMS),
    ]);
    return {
        privateKey: ePriv,
        publicKey: ePub,
    };
};
export const signatureKeyPairFromArray = async (publicKey, privateKey) => {
    const [sPriv, sPub] = await Promise.all([
        arrayToPrivateKey(privateKey, RSA_SIGNING_ALGO_PARAMS),
        arrayToPublicKey(publicKey, RSA_SIGNING_ALGO_PARAMS),
    ]);
    return {
        privateKey: sPriv,
        publicKey: sPub,
    };
};
export const encryptionKeyPairFromPrivateKey = async (privateKey) => {
    const privateKeyJwk = await cryptoKeyToJwk(privateKey);
    if (privateKeyJwk.kty !== 'RSA') {
        throw new InvalidCryptoKeyError();
    }
    const publicKey = await webCrypto.subtle.importKey('jwk', {
        e: privateKeyJwk.e,
        n: privateKeyJwk.n,
        kty: privateKeyJwk.kty,
    }, {
        name: RSA_ENCRYPTION_ALGO_NAME,
        hash: RSA_CRYPTO_ENCRYPT_HASH,
    }, true, ['encrypt']);
    return {
        privateKey,
        publicKey,
    };
};
export const cryptoPairsFromArray = async (publicKey, privateKey) => {
    const [encryptPair, signPair] = await Promise.all([
        encryptionKeyPairFromArray(publicKey, privateKey),
        signatureKeyPairFromArray(publicKey, privateKey),
    ]);
    return {
        encryptPair,
        signPair,
    };
};
